/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  ContactDetailsDialogConfig,
  contactDetailsDialogRoute,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ContactType,
  DICTIONARY_USER_STATUS_FOR_EDIT,
  PRIVILEGED_EP_USER_SEND_EMAILS_FROM_VERIFICATION,
  PRIVILEGED_EP_USER_UPDATE,
  PRIVILEGED_EP_USER_VERIFICATION,
  SystemRoleBit,
  systemRolesBitSet,
  UserAllowAccess,
  UserListItemDto,
  UserStatus,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  CanLeaveRoute,
  compoundLogicalAction,
  eitherAction,
  EnumDictionary,
  exitAction,
  getDefaultEmail,
  getRouteInitData,
  goToStep,
  isPrivilegedOperationAllowed,
  LogicalAction,
  resetPrivilegedTokensAndUpdate,
  streamValueAction,
  toAppFormControl,
  updatePrivilegedControls,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DEFAULT_EMAIL_SUFFIX } from '@gh/config';
import { isMessageBasket, MessageBasket, MessageSeverity } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  FormMode,
  getControl,
  getControlValue,
  noAnswer,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { merge$, throwError$ } from '@gh/rx';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';

import { assign, find, isEmpty, isNil, pick } from 'lodash';

import { OfficeRole } from '../../resources/office-role.model';
import { UserDetailsBusinessService } from '../../resources/user-details-business.service';
import {
  mergeUserDetailsWithSalesPersons,
  toUserIntranetCredentials,
  UserDetails,
  UserDetailsContact,
  UserDetailsIntranet,
  UserDetailsVerify,
  UserPosition,
} from '../../resources/user-details.model';
import { UserListItem } from '../../resources/user-list-item.model';
import { UserDetailsFormState } from './user-details-form.service';
import { UserDetailsIntranetFormState } from './user-details-intranet-form.service';
import { BitSet, SlicedData } from '@gh/core-data';

const CONFIRMATION_MAP = {
  'msg_user_transfer'  : 'skipChangeStatusCheck',
  'msg_user_inactivate': 'skipChangeStatusCheck',
};

@Component({
             selector     : 'gh-user-details',
             template: require('./user-details.component.html'),
             styles: [require('./user-details.component.scss')],
             viewProviders: [
               UserDetailsFormState,
               UserDetailsIntranetFormState,
             ],
           })
export class UserDetailsComponent extends AbstractFormComponent<UserDetails> implements OnInit, CanLeaveRoute {
  statuses: EnumDictionary<UserStatus>;
  officeRoles: OfficeRole[];
  positions: any;
  selectedPosition: UserPosition;
  systemRoles: EnumDictionary<UserSystemRole>;
  user: UserDetails;
  salesPersons: UserListItem[];
  needValidation: boolean = true;
  emailSuffix: string = DEFAULT_EMAIL_SUFFIX;
  allowAccessToLeads: EnumDictionary<UserAllowAccess>;
  systemRoleBit: BitSet<any>;
  isExistingUserChecked: boolean = false;

  get parentTitle(): string {
    const label = this.userContext.corporateView ? 'lbl_menu_corporate_users' : this.userContext.masterView ? 'lbl_menu_master_users' : 'lbl_menu_users';
    return this.translationService.get(label);
  }

  constructor(
    private route: ActivatedRoute,
    private router: AppRouter,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private waitingService: WaitingService,
    private businessService: UserDetailsBusinessService,
    public form: UserDetailsFormState,
    public intranetForm: UserDetailsIntranetFormState,
    private translationService: TranslationService,
    private navigationService: BuilderNavigationService,
    private userContext: BuilderUserContext) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<UserDetails>> {
    return this.pipelines.complete({
                                     validate: this.commonActions.validate(({ value }) => this.validate(value)),
                                     execute : this.saveAction,
                                     finish  : ({ value: user }) => {
                                       // reset form dirty flag
                                       this.form.group.markAsPristine();
                                       if (this.form.createMode) {
                                         // go to edit 'Edit User' page
                                         this.router.switchTo(['/setup/users', user.userId]);
                                       } else {
                                         // update current form
                                         if (user.status === UserStatus.Transferred) {
                                           this.form.group.disable();
                                         }
                                         if (user.position) {
                                           this.selectedPosition = user.position;
                                           // @ts-ignore
                                           user.position = this.selectedPosition.itemCode;
                                         }
                                         this.form.build(
                                           mergeUserDetailsWithSalesPersons(user, this.salesPersons || []));
                                         this.intranetForm.build(toUserIntranetCredentials(user), user);

                                         resetPrivilegedTokensAndUpdate(this.form);
                                         resetPrivilegedTokensAndUpdate(this.intranetForm);
                                         this.intranetForm.markAsRequired(false);
                                         this.intranetForm.markAsPristine();
                                       }
                                     },
                                   });
  }

  get saveAction(): LogicalAction<FormFlowState<UserDetails>> {
    return compoundLogicalAction('save.success', 'save.fail', {
      'start': goToStep('save'),
      // try to save
      'save': this.commonActions.execute(({ value }: FormFlowState<UserDetails>) => {
        const saveObservable = this.isExistingUserChecked
          ? this.businessService.saveExistingUserAccount(
            value,
            this.userContext.officeView || value.position ? this.userPosition : undefined
          )
          : this.businessService.save(
            value,
            this.userContext.officeView || value.position ? this.userPosition : undefined
          );

        // Return the save observable
        return saveObservable;
      }).bindAs('success', 'ifShowConfirmation'),
      // if we have confirmation error => show confirmation, otherwise => save fails
      'ifShowConfirmation': eitherAction(({ error }) =>
                                           isMessageBasket(error) && error.messages.some(
                                             (msg) => msg.severity === MessageSeverity.Confirmation))
      .bindAs('confirmation', 'fail'),
      // show confirmation
      'confirmation': streamValueAction((state: FormFlowState<UserDetails>) => {
        const { value, error } = state;
        // tslint:disable-next-line:no-non-null-assertion
        const confirmation = find(error.messages, (msg) => msg.severity === MessageSeverity.Confirmation)!;
        const confirmation$ = this.dialogService.confirmation(yesNoConfirmation(confirmation.key, confirmation.params));
        // if user agrees with confirmation => try to save again
        const yes$ = confirmation$.pipe(filter$(yesAnswer), map$(() => {
          // set confirmation flag and execute save operation from the beginning
          const controlName = CONFIRMATION_MAP[confirmation.key];
          return {
            ...state,
            value: {
              ...value,
              confirmations: {
                ...value.confirmations,
                [controlName]: true,
              },
            },
            error: void 0,
          };
        }));
        // if user disagrees confirmation just stop save operation
        const no$ = confirmation$.pipe(filter$(noAnswer), switchMap$(() => throwError$(void 0)));
        return merge$(yes$, no$);
      }).bindAs('save', 'fail'),
      'success'     : exitAction('save.success'),
      '*'           : exitAction('save.fail'),
    });
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_user_create',
      [FormMode.EditMode]  : 'lbl_user_edit',
      [FormMode.ViewMode]  : 'lbl_user_view',
    }[this.form.mode];
  }

  get email(): string {
    return getDefaultEmail(getControlValue(this.form.group, 'userEmail'));
  }

  get sendEmailsFrom(): string {
    return getDefaultEmail(getControlValue(this.form.group, 'sendEmailsFrom'));
  }

  get officeView(): boolean {
    return this.userContext.officeView;
  }

  get isEditMode(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Setup.Manager.UserDetails.EDIT);
  }

  get canIntranetVerify(): boolean {
    return !this.form.isIntranetVerified
      || isPrivilegedOperationAllowed(this.intranetForm, PRIVILEGED_EP_USER_VERIFICATION);
  }

  get isPrivilegedFlowRequired(): boolean {
    return this.userContext.officeView && !this.userContext.isSwitched()
      && isNil(getControlValue(this.form.group, 'privilegedTokens.UserUpdate'));
  }

  get isSendEmailsFromPrivilegedFlowRequired(): boolean {
    return this.userContext.officeView && !this.userContext.isSwitched()
      && isNil(getControlValue(this.form.group, 'privilegedTokens.UserAccessToMultipleOffices'));
  }

  get canChangeSendEmailsFrom(): boolean {
    return (this.userContext.isSwitched() || this.userContext.hasSystemRole(SystemRoleBit.Franchisee))
      && this.form.editable
      && this.form.allowAccessToMultipleOffices;
  }

  get isExistingUser(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate) || 
    this.userContext.hasSystemRole(SystemRoleBit.Master);
  }

  get userPosition(): UserPosition {
    const index = this.positions.items.findIndex((el: any) => this.form.value.position === el.id);
    return {
      code        : 'USER_POSITION',
      itemCode    : this.positions.items[index].id,
      localization: this.positions.items[index].name,
    };
  }

  get sortedPositionItems(): any[] {
    return this.positions?.items.sort((a: any, b: any) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  }

  get isReassignSalesVisible(): boolean {
    const status = this.form.group.get('status')?.value;
    return !!this.user.systemRoles.Salesperson &&
      (status === UserStatus.Inactive || status === UserStatus.Transferred || status === UserStatus.Deleted);
  }

  onPrivilegedTokenReceived(): void {
    updatePrivilegedControls(this.form, PRIVILEGED_EP_USER_UPDATE);
  }

  onIntranetPrivilegedTokenReceived(): void {
    updatePrivilegedControls(this.intranetForm, PRIVILEGED_EP_USER_VERIFICATION);
  }

  onSendEmailsFromTokenReceived(): void {
    updatePrivilegedControls(this.form, PRIVILEGED_EP_USER_SEND_EMAILS_FROM_VERIFICATION);
  }

  ngOnInit(): void {
    const { user, statuses, salesPersons, ...init } = getRouteInitData(this.route);

    assign(this, init);
    this.user = user;

    this.statuses = DICTIONARY_USER_STATUS_FOR_EDIT.filter(statuses, {
      applicationView: this.userContext.applicationView,
      status         : user.status,
    });

    this.salesPersons = salesPersons;

    if (user.position) {
      this.selectedPosition = user.position;
      user.position = this.selectedPosition.itemCode;
    }

    this.form.build(mergeUserDetailsWithSalesPersons(user, salesPersons || []));
    if (this.user.systemRoles[UserSystemRole.Corporate]) {
      this.businessService.findAllCorporate().select({from: 0, to: 2}).subscribe((res) => {
        if (res.data.length === 1) {
          this.form.disableStatusList();
        }
      });
    }
    this.intranetForm.build(toUserIntranetCredentials(user), user);
    if (this.user.systemRoles[UserSystemRole.Corporate]) {
      this.systemRoleBit = systemRolesBitSet(SystemRoleBit.Corporate);
    } else if (this.user.systemRoles[UserSystemRole.Master]) {
      this.systemRoleBit = systemRolesBitSet(SystemRoleBit.Master);
    } else if (this.user.systemRoles[UserSystemRole.Franchisee]) {
      this.systemRoleBit = systemRolesBitSet(SystemRoleBit.Franchisee);
    }
  }

  viewContactDetails(): void {
    this.openContact(this.form.getContactId());
  }

  createContact(): void {
    this.openContact();
  }

  onAllowAccessToLeadsChange(): void {
    this.form.updateAllowAccessToLeads();
  }

  onContactSelected(contact: UserDetailsContact): void {
    this.form.setContact(contact);
  }

  onReassignSalesSelected(person: UserDetails): void {
    this.form.reassignSales(person);
  }

  onChangeSystemRole(): void {
    this.form.changeSystemRole();
    this.intranetForm.recalculate(this.form.submitValue);
  }

  onChangeExistingUser({ checked }: MatCheckboxChange): void {
    this.isExistingUserChecked = checked;
    this.form.existingUserCheck(checked);
  }

  changeUserDataToTransferState(res: SlicedData<UserListItemDto>): void {
    if (res.data.length > 1) {
      this.form.enableUserDataToTransfer();
    } else {
      this.form.disableUserDataToTransfer();
    }
  }
  onChangeStatus(control: any): void {
    if (this.user.firstInLocation && (control.value === UserStatus.Inactive || control.value === UserStatus.Transferred)) {
      if (this.user.systemRoles[UserSystemRole.Corporate]) {
        this.businessService.findAllCorporate().select({from: 0, to: 2}).subscribe((res) => this.changeUserDataToTransferState(res));
      } else if (this.user.systemRoles[UserSystemRole.Master]) {
        this.businessService.findAllMaster().select({from: 0, to: 2}).subscribe((res) => this.changeUserDataToTransferState(res));
      } else if (this.user.systemRoles[UserSystemRole.Franchisee]) {
        this.businessService.findAllFranchisee().select({from: 0, to: 2}).subscribe((res) => this.changeUserDataToTransferState(res));
      }
    } else {
      this.form.disableUserDataToTransfer();
    }
    this.form.changeStatus(control);
    this.intranetForm.recalculate(this.form.submitValue);
  }

  loadContacts($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    load(this.businessService.findEmployeeContactsByName(query));
  }

  loadReassignContacts($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    load(this.businessService.findSalesAndFirstFranchiseeByName(query, this.user));
  }

  loadUsers($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    const fullQuery = {
      ...query,
      filter: {
        name        : query.filter,
        showInactive: false,
        systemRoles : this.systemRoleBit,
        excludeIds  : [(this.form.get('userId') as any).value],
      },
    };

    load(this.businessService.findInboxUsersByName(fullQuery));
  }

  loadTransferUsers($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    const fullQuery = {
      ...query,
      filter: {
        name        : query.filter,
        showInactive: false,
        autocomplete: true,
        excludeIds  : [(this.form.get('userId') as any).value],
        systemRoles : this.systemRoleBit,
      },
    };

    load(this.businessService.findTransferUsersByName(fullQuery));
  }

  createAccountForExistingUser(user: UserDetails): void {
    if (isEmpty(user.userEmail)) {
    this.businessService.createAccountForExistingUser(user);
  }
}

  verifyUser(): void {
    if (this.intranetForm.invalid) {
      this.intranetForm.markAsRequired(true);
      this.intranetForm.recalculate(this.form.submitValue);
      this.intranetForm.markAllAsTouched();
      return;
    }

    this.businessService
        .verifyUser(pick(assign({}, this.form.submitValue, this.intranetForm.submitValue), [
          'userId',
          'intranetUser',
          'intranetPassword',
        ]) as UserDetailsVerify).pipe(
      (this.waitingService.operator()))
        .subscribe(
          (id: UserDetailsIntranet) => {
            this.form.updateIntranetInfo(this.intranetForm.submitValue, id.intranetGuid);
            this.intranetForm.recalculate(this.form.submitValue);
            resetPrivilegedTokensAndUpdate(this.intranetForm);
            this.intranetForm.markAsRequired(false);
            this.intranetForm.markAsPristine();
          },
          () => this.dialogService.error('msg_user_intranet_not_verified'),
        );
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
                                     entityId  : entityId ? entityId : undefined,
                                     entityType: entityType ? entityType : 'USER',
                                     label     : label ? label : 'lbl_users_list',
                                   });
  }

  onChangeAllowMultipleOffices({ checked }: MatCheckboxChange): void {
    toAppFormControl(getControl(this.form.group, 'sendEmailsFrom'))
    .updateSchema({ visible: checked });
  }

  sendEmailsFromPlaceholder(): string {
    return `${this.translationService.get('lbl_send_emails_from')} `;
  }

  isSelectedUser({ userId }: UserListItem): boolean {
    return this.user.userId === userId;
  }

  private validate(user: UserDetails): Maybe<MessageBasket> {
    if (isEmpty(user.userEmail)) {
      return MessageBasket.error('msg_login_required');
    } else if (user.allowAccessToMultipleOffices && isEmpty(user.sendEmailsFrom)) {
      return MessageBasket.error('msg_login_required');
    } else {
      return void 0;
    }
  }

  private openContact(openedContact?: UserDetailsContact): void {
    this.dialogService.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      entryPoint              : 'user',
      contactType             : ContactType.Employee,
      contactId               : openedContact ? openedContact.id : void 0,
      parentTitle             : this.title,
      skipOfficeSetupFlowCheck: true,
    }).afterClosed().pipe(
      filter$(Boolean))
        .subscribe((contact: any) => {
          this.form.setContact({
                                 id       : contact.contactId,
                                 name     : contact.name,
                                 firstName: contact.firstName,
                                 lastName : contact.lastName,
                               });
        });
  }
}
